import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Hammer, Spinner, UsersThree, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthDirect } = useAuth();
  const [token] = useState(() => params.get("token") || "");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Accept invite · ClickForge";
    if (token) window.history.replaceState({}, "", "/accept-invite");
    if (!token) {
      setPreviewError("This invitation link is missing a token.");
      return;
    }
    api
      .post("/auth/invite-preview", { token })
      .then((r) => setPreview(r.data))
      .catch((err) =>
        setPreviewError(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid or expired invitation"),
      );
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return setError("This invitation is missing a token.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password });
      if (data.token) {
        // Auto-sign in
        localStorage.setItem("cf_token", data.token);
        localStorage.setItem("cf_user", JSON.stringify(data.user));
        if (setAuthDirect) setAuthDirect(data.user, data.token);
        toast.success(`Welcome to ClickForge, ${data.user?.name || "teammate"}.`);
        navigate("/dashboard", { replace: true });
      } else {
        toast.success("Account created. Please sign in.");
        navigate("/login", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950 text-zinc-100">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
            <Hammer size={20} weight="bold" className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">ClickForge</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400">Lead Agent</div>
          </div>
        </div>

        {previewError ? (
          <div data-testid="invite-invalid-panel" className="border border-red-500/30 bg-red-500/5 rounded-sm p-6 text-center space-y-3">
            <h2 className="font-display text-xl font-semibold text-zinc-50">Invitation invalid</h2>
            <p className="text-sm text-zinc-400">{previewError}</p>
            <Link to="/login" className="inline-block text-orange-400 hover:text-orange-300 text-sm font-medium underline-offset-2 hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : !preview ? (
          <div data-testid="invite-loading" className="text-center text-zinc-500 text-sm py-6">
            <Spinner size={20} className="animate-spin mx-auto mb-2" />
            Checking invitation…
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-500 mb-2 flex items-center gap-1.5">
                <UsersThree size={12} weight="bold" /> Teammate invitation
              </div>
              <h2 className="font-display text-3xl font-semibold text-zinc-50">Welcome, {preview.name}</h2>
              <p className="text-sm text-zinc-500 mt-2">
                Michael invited <span className="text-zinc-200 font-mono text-xs">{preview.email}</span> to ClickForge.
                Set a password to activate your isolated pipeline.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-form" autoComplete="new-password">
              <div>
                <Label htmlFor="pw" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Create password</Label>
                <Input
                  id="pw"
                  data-testid="invite-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500"
                />
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Repeat password</Label>
                <Input
                  id="pw2"
                  data-testid="invite-confirm-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500"
                />
              </div>

              {error && (
                <div data-testid="invite-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-sm px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="invite-submit-btn"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium rounded-sm h-10"
              >
                {loading ? (<><Spinner size={16} className="mr-2 animate-spin" /> Creating…</>) : (<><CheckCircle size={16} className="mr-2" /> Create account & sign in</>)}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
